import { useEffect, useState } from 'react';

import {
  faCircleHalfStroke,
  faDropletSlash,
  faHighDefinition,
  faImage,
  faMagnifyingGlass,
  faReflectHorizontal,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faBrightness,
  faRotateBack,
  faRotateRight,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { faArrowDownRight } from '@awesome.me/kit-f3dc60f0d0/icons/duotone/solid';
import { faExpand, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Slider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'react-use-keypress'
import useKeypress from 'react-use-keypress';

import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '@/app/appHeader/imageViewerMenu/atoms';
import { SharpnessFilterSelectionModal } from '@/app/appHeader/practicePreferencesMenu/imageFilterSelection/SharpnessFilterSelection';
import {
  DEFAULT_BRIGHTNESS_VALUE,
  DEFAULT_CONTRAST_VALUE,
  MAX_BRIGHTNESS_VALUE,
  MAX_CONTRAST_VALUE,
  MIN_BRIGHTNESS_VALUE,
  MIN_CONTRAST_VALUE,
} from '@/constants';
import { isDirectorUser as isDirectorUserUtil } from '@/modules/login/login-utils';
import { usePreferences } from '@/react-query/usePreferences';
import { useRestrictClinicalInteractionsForDirectors } from '@/utils/hooks';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ReactComponent as HD1 } from '../../../../src/svgs/HD1.svg';
import { ReactComponent as HD2 } from '../../../../src/svgs/HD2.svg';
import { isWebImageType } from '../annotation-utils/annotation-utils';
import annotatorConstants from '../annotator.constants';
import { showPastVisitsAtom, viewerModeAtom } from '../atoms';
import { disableKeyPressAtom } from '../casePresentation/atoms';
import {
  brightnessValueAtom,
  contrastValueAtom,
  enabledHDAtom,
  isFullScreenOpenAtom,
  isHQEnabledAtom,
  isOpacityInvertedAtom,
} from '../multiFileViewer/imageToolbar/atoms';
import { useRotation } from '../multiFileViewer/RotationContext';
import type { ImageObject } from '../types';

import { Shortcuts } from './Shortcuts';
import { ToolbarButton } from './ToolbarButton';

export const ImageControls = ({
  activeImage,
  setShouldPersistUserModifications,
  onChangeViewerMode,
}: {
  activeImage: ImageObject;
  setShouldPersistUserModifications: (value: boolean) => void;
  onChangeViewerMode: (mode: number) => void;
  shouldIncludeShortcuts?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [brightnessValue, setBrightnessValue] = useAtom(brightnessValueAtom);
  const [contrastValue, setContrastValue] = useAtom(contrastValueAtom);
  const [isOpacityInverted, setIsOpacityInverted] = useAtom(
    isOpacityInvertedAtom,
  );
  const [isHQEnabled, setIsHQEnabled] = useAtom(isHQEnabledAtom);
  const [enabledHD, setEnabledHD] = useAtom(enabledHDAtom);
  const [showPastVisits] = useAtom(showPastVisitsAtom);
  const [viewerMode, setViewerMode] = useAtom(viewerModeAtom);
  const [, setIsFullScreenOpen] = useAtom(isFullScreenOpenAtom);
  const selectedVisit = useAtomValue(selectedVisitAtom);
  const selectedUnifiedId = useAtomValue(selectedUnifiedIdAtom);
  const [showBrightnessSlider, setShowBrightnessSlider] = useState(false);
  const [showContrastSlider, setShowContrastSlider] = useState(false);
  const user = useSelector((state: any) => state.login.user);
  const isDisableKeyPress = useAtomValue(disableKeyPressAtom);

  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const { data: preferences } = usePreferences();
  const isCdnEnabled = !!preferences?.cdnEnabled;
  const isHQDisabledByDefault = !!preferences?.hqOffByDefault;
  const {
    resetImageRotation,
    currentlyRotatingImages,
    resetImageRotationState,
    rotatedImages,
    rotateImage,
    mirroredImages,
    toggleImageMirror,
    resetImageMirror,
  } = useRotation();
  const isRestrictClinicalInteractions =
    useRestrictClinicalInteractionsForDirectors();

  const isImageMirrored = mirroredImages[activeImage?.objectId] || false;
  const isToolbarNotDefault =
    brightnessValue !== DEFAULT_BRIGHTNESS_VALUE ||
    contrastValue !== DEFAULT_CONTRAST_VALUE ||
    isOpacityInverted ||
    isImageMirrored ||
    isHQEnabled === !!isHQDisabledByDefault ||
    (activeImage?.imageId && !!rotatedImages[activeImage.imageId]) ||
    (showPastVisits && !!currentlyRotatingImages?.imageIds.length);

  const resetToolbarSettings = () => {
    if (!isToolbarNotDefault) {
      return;
    }
    trackEvent(mixpanelEvents.imageControlsReset, {
      source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
        } - toolbar`,
      previousSettings: {
        brightness: brightnessValue,
        contrast: contrastValue,
        isHQEnabled,
        isOpacityInverted,
        hasRotation: activeImage?.imageId
          ? !!rotatedImages[activeImage.imageId]
          : false,
      },
    });
    setBrightnessValue(DEFAULT_BRIGHTNESS_VALUE);
    setContrastValue(DEFAULT_CONTRAST_VALUE);
    setIsHQEnabled(!isHQDisabledByDefault);
    if (isOpacityInverted) {
      setIsOpacityInverted(false);
    }
    if (isImageMirrored) {
      resetImageMirror(activeImage?.objectId);
    }
    if (showPastVisits) {
      resetImageRotationState();
    } else if (activeImage?.imageId) {
      resetImageRotation(activeImage.imageId);
    }
    if (!showPastVisits) {
      setShouldPersistUserModifications(true);
    }
  };

  useKeypress(['D', 'd'], () => {
    if (isDisableKeyPress) return;
    resetToolbarSettings();
  });

  let shouldDisableHQButton = false;
  let shouldDisableHDButton = false;
  let hqTooltipTitle = isHQEnabled
    ? t`Show original image (H)`
    : t`Show sharpened image (H)`;
  let hd1TooltipTitle =
    enabledHD === 'HD1' ? t`Show original image` : t`Show HD1 sharpened image`;
  let hd2TooltipTitle =
    enabledHD === 'HD2' ? t`Show original image` : t`Show HD2 sharpened image`;
  const showCreateFindingButton =
    activeImage.detectedImageType === 'BITEWING' ||
    activeImage.detectedImageType === 'PERIAPICAL';
  const isDirectorUser = isDirectorUserUtil(user);

  if (!isCdnEnabled && !activeImage?.imageKeys?.generalized?.key) {
    hqTooltipTitle = t`Sharpened image not available`;
    hd1TooltipTitle = t`Sharpened image not available`;
    hd2TooltipTitle = t`Sharpened image not available`;
    shouldDisableHQButton = true;
    shouldDisableHDButton = true;
  }
  if (
    !isCdnEnabled &&
    !activeImage?.imageKeys?.sharpened?.key &&
    (!activeImage?.imageKeys?.original?.key ||
      !isWebImageType(activeImage.imageKeys.original.key))
  ) {
    hqTooltipTitle = t`Original image not available`;
    hd1TooltipTitle = t`Original image not available`;
    hd2TooltipTitle = t`Original image not available`;
    shouldDisableHQButton = true;
    shouldDisableHDButton = true;
  }
  if (activeImage.detectedImageType === 'COLOR_IMAGE') {
    shouldDisableHQButton = true;
    shouldDisableHDButton = true;
  }

  // Need to default the HQ value once we get prefs back
  useEffect(() => {
    // Wait for preferences + active image to be defined before setting HQ toggle
    // Don't reset the HQ toggle once it's been set
    if (
      !preferences ||
      (!activeImage?.imageId && !preferences.enableHqOnThumbnails)
    ) {
      return;
    }
    if (enabledHD === 'HD2' && !activeImage?.sharpenedHD2Path) {
      setEnabledHD('HD1');
      return;
    }

    if (
      (isHQEnabled !== undefined && !preferences?.adjustmentVersionHD2) ||
      (enabledHD !== undefined && preferences?.adjustmentVersionHD2)
    ) {
      return;
    }

    let isHQDefault = !preferences?.hqOffByDefault;
    if (activeImage?.userModifications) {
      isHQDefault =
        activeImage?.userModifications?.hqEnabled ??
        !preferences?.hqOffByDefault;
    }

    setIsHQEnabled(isHQDefault);
    setEnabledHD(isHQDefault ? 'HD1' : false);

    // Don't want to trigger effect anytime activeImage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeImage?.userModifications?.hqEnabled,
    activeImage?.imageId,
    activeImage?.sharpenedHD2Path,
    preferences,
    setIsHQEnabled,
    setEnabledHD,
  ]);

  const toggleHighImageQuality = () => {
    if (shouldDisableHQButton) {
      return;
    }
    const isNewHQEnabled = !isHQEnabled;
    if (!showPastVisits) {
      setShouldPersistUserModifications(true);
    }
    setIsHQEnabled(isNewHQEnabled);
    trackEvent(mixpanelEvents.hqToggle, {
      isHQEnabled: isNewHQEnabled,
      source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
        } - toolbar`,
    });
  };

  const selectHDVersion = ({ value }: { value: 'HD1' | 'HD2' }) => {
    const newHD = value === enabledHD ? undefined : value;
    if (shouldDisableHDButton) {
      return;
    }
    setEnabledHD(newHD ?? false);
    trackEvent(mixpanelEvents.hdVersionSelected, {
      source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
        } - toolbar`,
      value: newHD,
    });
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild data-testid="image-controls-trigger">
            <div className="vai-relative">
              <ToolbarButton isActive={isOpen}>
                <FontAwesomeIcon
                  icon={faImage}
                  className="vai-text-slate-200 max-[880px]:vai-text-base min-[880px]:vai-text-lg"
                />
                <FontAwesomeIcon
                  icon={faArrowDownRight}
                  className="vai-text-slate-300 vai-absolute max-[880px]:vai-right-[-2px] max-[880px]:vai-bottom-[-2px] min-[880px]:vai-right-[-3px] min-[880px]:vai-bottom-[-3px] max-[880px]:!vai-size-1.5 min-[880px]:!vai-size-2.5 [--fa-secondary-opacity:0]"
                />
              </ToolbarButton>
            </div>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent
          variant="dark"
          side="right"
          sideOffset={8}
          alignOffset={-4}
        >
          <p>{t`Image controls`}</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        className="!vai-bg-[#393E4D] !vai-border-none !vai-w-fit !vai-flex vai-gap-1 !vai-p-1 !vai-m-0"
        side="right"
        align="start"
        sideOffset={8}
        alignOffset={-4}
        data-testid="image-controls-popover"
      >
        {!preferences?.adjustmentVersionHD2 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ToolbarButton
                  dataTestId="HQ-trigger"
                  disabled={shouldDisableHQButton}
                  icon={faHighDefinition}
                  isActive={!!isHQEnabled}
                  onClick={() => toggleHighImageQuality()}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              variant="dark"
              side="top"
              sideOffset={8}
              alignOffset={-4}
            >
              <p>{hqTooltipTitle}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {preferences?.adjustmentVersionHD2 && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ToolbarButton
                    dataTestId="HD1-trigger"
                    iconComponent={<HD1 />}
                    isActive={enabledHD === 'HD1'}
                    disabled={
                      shouldDisableHDButton || !activeImage.sharpenedPath
                    }
                    onClick={() => selectHDVersion({ value: 'HD1' })}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent
                variant="dark"
                side="top"
                sideOffset={8}
                alignOffset={-4}
              >
                <p>{hd1TooltipTitle}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ToolbarButton
                    iconComponent={<HD2 />}
                    isActive={enabledHD === 'HD2'}
                    disabled={
                      shouldDisableHDButton || !activeImage.sharpenedHD2Path
                    }
                    onClick={() => selectHDVersion({ value: 'HD2' })}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent
                variant="dark"
                side="top"
                sideOffset={8}
                alignOffset={-4}
              >
                <p>{hd2TooltipTitle}</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}
        {preferences?.enableSharpnessFilterSelectionModal && (
          <SharpnessFilterSelectionModal isInImageToolbar={true} />
        )}

        <div className="vai-w-px vai-bg-[#272A34]" />

        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                dataTestId="reset-toolbar-settings"
                disabled={!isToolbarNotDefault}
                icon={faRotateBack}
                isActive={false}
                onClick={() => resetToolbarSettings()}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Reset all settings (D)`}</p>
          </TooltipContent>
        </Tooltip>

        <div className="vai-relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ToolbarButton
                  dataTestId="brightness-slider-trigger"
                  icon={faBrightness}
                  isActive={
                    showBrightnessSlider ||
                    brightnessValue !== DEFAULT_BRIGHTNESS_VALUE
                  }
                  onClick={() => {
                    setShowBrightnessSlider(!showBrightnessSlider);
                    setShowContrastSlider(false);
                    trackEvent(mixpanelEvents.brightnessSliderToggle, {
                      source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                        } - toolbar`,
                      isVisible: !showBrightnessSlider,
                    });
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              variant="dark"
              side="top"
              sideOffset={8}
              alignOffset={-4}
            >
              <p>{t`Adjust brightness (1-6)`}</p>
            </TooltipContent>
          </Tooltip>
          {showBrightnessSlider && (
            <div className="vai-absolute vai-left-1/2 vai-transform vai--translate-x-1/2 vai-top-full vai-mt-2 vai-bg-black vai-p-4 vai-rounded-md vai-shadow-lg vai-z-50 vai-w-48">
              <Slider
                data-testid="brightness-slider"
                defaultValue={[brightnessValue]}
                min={MIN_BRIGHTNESS_VALUE}
                max={MAX_BRIGHTNESS_VALUE}
                step={1}
                onValueChange={(values) => {
                  setBrightnessValue(values[0]);
                  if (!showPastVisits) {
                    setShouldPersistUserModifications(true);
                  }
                  trackEvent(mixpanelEvents.brightnessAdjusted, {
                    source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                      } - toolbar`,
                    value: values[0],
                  });
                }}
              />
            </div>
          )}
        </div>

        <div className="vai-relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ToolbarButton
                  dataTestId="contrast-slider-trigger"
                  icon={faCircleHalfStroke}
                  isActive={
                    showContrastSlider ||
                    contrastValue !== DEFAULT_CONTRAST_VALUE
                  }
                  onClick={() => {
                    setShowContrastSlider(!showContrastSlider);
                    setShowBrightnessSlider(false);
                    trackEvent(mixpanelEvents.contrastSliderToggle, {
                      source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                        } - toolbar`,
                      isVisible: !showContrastSlider,
                    });
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              variant="dark"
              side="top"
              sideOffset={8}
              alignOffset={-4}
            >
              <p>{t`Adjust contrast`}</p>
            </TooltipContent>
          </Tooltip>
          {showContrastSlider && (
            <div className="vai-absolute vai-left-1/2 vai-transform vai--translate-x-1/2 vai-top-full vai-mt-2 vai-bg-black vai-p-4 vai-rounded-md vai-shadow-lg vai-z-50 vai-w-48">
              <Slider
                data-testid="contrast-slider"
                defaultValue={[contrastValue]}
                min={MIN_CONTRAST_VALUE}
                max={MAX_CONTRAST_VALUE}
                step={1}
                onValueChange={(values) => {
                  setContrastValue(values[0]);
                  if (!showPastVisits) {
                    setShouldPersistUserModifications(true);
                  }
                  trackEvent(mixpanelEvents.contrastAdjusted, {
                    source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                      } - toolbar`,
                    value: values[0],
                  });
                }}
              />
            </div>
          )}
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                dataTestId="invert-colors-trigger"
                icon={faDropletSlash}
                isActive={isOpacityInverted}
                onClick={() => {
                  const isNewValue = !isOpacityInverted;
                  setIsOpacityInverted(isNewValue);
                  trackEvent(mixpanelEvents.opacityInverted, {
                    source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                      } - toolbar`,
                    isInverted: isNewValue,
                  });
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Invert colors (I)`}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                icon={faMagnifyingGlass}
                isActive={viewerMode === annotatorConstants.MODES.MAGNIFYING}
                onClick={() => {
                  const newMode =
                    viewerMode === annotatorConstants.MODES.MAGNIFYING
                      ? annotatorConstants.MODES.VIEWING
                      : annotatorConstants.MODES.MAGNIFYING;
                  setViewerMode(newMode);
                  trackEvent(mixpanelEvents.magnifyingModeToggle, {
                    source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                      } - toolbar`,
                    mode: newMode,
                  });
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Toggle magnify mode (M)`}</p>
          </TooltipContent>
        </Tooltip>

        {!showPastVisits && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ToolbarButton
                  dataTestId="rotate-image-trigger"
                  icon={faRotateRight}
                  isActive={false}
                  onClick={() => {
                    rotateImage(activeImage);
                    trackEvent(mixpanelEvents.imageRotated, {
                      source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                        } - toolbar`,
                      imageId: activeImage?.imageId,
                    });
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              variant="dark"
              side="top"
              sideOffset={8}
              alignOffset={-4}
            >
              <p>{t`Rotate image (.)`}</p>
            </TooltipContent>
          </Tooltip>
        )}

        <div className="vai-w-px vai-bg-[#272A34]" />

        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                dataTestId="mirror-image-trigger"
                icon={faReflectHorizontal}
                isActive={isImageMirrored}
                onClick={() => {
                  setShouldPersistUserModifications(true);
                  toggleImageMirror(activeImage?.objectId);
                  trackEvent(mixpanelEvents.imageMirrored, {
                    source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                      } - toolbar`,
                    imageId: activeImage?.imageId,
                  });
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`Mirror image`}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                dataTestId="full-screen-trigger"
                icon={faExpand}
                isActive={false}
                onClick={() => {
                  trackEvent(mixpanelEvents.clickFullScreenButton, {
                    imageId: activeImage.imageId,
                    imageType: activeImage.detectedImageType,
                    source: `${!activeImage ? 'multiFileViewer' : 'singleImageViewer'
                      } - toolbar`,
                  });
                  setIsFullScreenOpen(true);
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>{t`View in full screen`}</p>
          </TooltipContent>
        </Tooltip>

        {showCreateFindingButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ToolbarButton
                  dataTestId="create-finding-trigger"
                  disabled={isRestrictClinicalInteractions}
                  icon={faPlus}
                  isActive={viewerMode === annotatorConstants.MODES.DRAWING}
                  onClick={() => {
                    if (isRestrictClinicalInteractions) return;

                    const newMode =
                      viewerMode === annotatorConstants.MODES.DRAWING
                        ? annotatorConstants.MODES.VIEWING
                        : annotatorConstants.MODES.DRAWING;
                    onChangeViewerMode(newMode);
                    trackEvent(mixpanelEvents.clickCreateFindingButton, {
                      findingType: 'caries',
                      mode: newMode,
                      estimatedVisitId: selectedVisit,
                      unifiedAppointmentId: selectedUnifiedId,
                    });
                  }}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent
              variant="dark"
              side="top"
              sideOffset={8}
              alignOffset={-4}
            >
              <p>
                <Trans>Create caries finding</Trans>
              </p>
            </TooltipContent>
          </Tooltip>
        )}
        <div>
          <Shortcuts />
        </div>
      </PopoverContent>
    </Popover>
  );
};
